import React, { useState, useEffect } from "react";
import { BottomSheet } from "../../../shared/components/BottomSheet.jsx";
import { Button } from "../../../shared/components/Button.jsx";
import { numberToIndianWords } from "../../../shared/utils/numberToWords.js";
import { INVOICE_TEMPLATES } from "../../companies/models/company.js";
import { getInvoiceTemplateById } from "../templates/registry.js";
import { shareInvoicePdf, downloadInvoicePdf } from "../../../shared/utils/pdfShare.js";
import { formatAppDate } from "../../../shared/utils/dateUtils.js";
import { useCompanies } from "../../companies/hooks/useCompanies.js";
import { mergeLineItemsForPrint } from "../utils/mergeLineItems.js";

const COPY_LABELS = {
  1: "ORIGINAL FOR RECIPIENT",
  2: "DUPLICATE FOR TRANSPORTER",
  3: "TRIPLICATE FOR SUPPLIER"
};

/** watermark: show a read-only earlier version (no download / share / print), e.g. "OLD VERSION". */
export function InvoicePrintModal({ isOpen, onClose, invoice, watermark = "" }) {
  if (!invoice) return null;

  const { companies, defaultCompany } = useCompanies();

  // Derive issuing company details (check live company collection if invoice snapshot lacks details)
  const invoiceCompany = invoice.companyDetails || invoice.sellerConfig || {};
  const activeCompany = (companies || []).find((c) => c.id === invoice.companyId) ||
                        (companies || []).find((c) => c.name === (invoice.companyName || invoiceCompany.name)) ||
                        defaultCompany ||
                        invoiceCompany;

  const company = {
    ...activeCompany,
    ...invoiceCompany
  };

  const companyName = invoice.companyName || invoiceCompany.name || activeCompany?.name || "SREE RAM TRADERS";
  const tradeName = invoiceCompany.tradeName || activeCompany?.tradeName || "";
  const addressLine1 = invoiceCompany.addressLine1 || invoiceCompany.address || activeCompany?.addressLine1 || "177-B, P.A.C.R. SALAI, (WEST)";
  const city = invoiceCompany.city || activeCompany?.city || "CHETTIYARPATTI - 626 122";
  const state = invoiceCompany.state || activeCompany?.state || "Tamil Nadu";
  const stateCode = invoiceCompany.stateCode || activeCompany?.stateCode || "33";
  const pincode = (invoiceCompany.pincode || activeCompany?.pincode) ? ` - ${invoiceCompany.pincode || activeCompany?.pincode}` : "";
  const fullAddress = `${addressLine1}, ${city}${pincode}`;
  const companyGstin = invoice.companyGstin || invoiceCompany.gstin || activeCompany?.gstin || "";
  const companyPhone = invoiceCompany.phone || activeCompany?.phone || "9865460487";
  const bankName = invoiceCompany.bankName || activeCompany?.bankName || "";
  const accountNumber = invoiceCompany.accountNumber || activeCompany?.accountNumber || "";
  const ifscCode = invoiceCompany.ifscCode || activeCompany?.ifscCode || "";
  const branchName = invoiceCompany.branchName || activeCompany?.branchName || "";

  // Signature URL (resolves from invoice, invoice company snapshot, or live company profile)
  const signatureUrl = invoice.signatureUrl || invoiceCompany.signatureUrl || activeCompany?.signatureUrl || "";

  // Date formatting pattern specific to company
  const companyDateFormat = invoiceCompany.dateFormat || activeCompany?.dateFormat || "DD-MM-YYYY";
  const formattedInvoiceDate = formatAppDate(invoice.invoiceDate, companyDateFormat);

  // Configured default copies & template (prioritizes company details configured template)
  const configuredTemplate = activeCompany?.defaultTemplate || activeCompany?.invoiceTemplate || invoiceCompany.defaultTemplate || invoice.defaultTemplate || "modern-tax";
  const configuredCopies = Number(activeCompany?.invoiceCopies || invoiceCompany.invoiceCopies || 2);

  const [selectedCopiesCount, setSelectedCopiesCount] = useState(configuredCopies);
  const [selectedTemplate, setSelectedTemplate] = useState(configuredTemplate);
  const [fitScreen, setFitScreen] = useState(false);
  const [isSharingPdf, setIsSharingPdf] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);

  useEffect(() => {
    if (configuredTemplate) {
      setSelectedTemplate(configuredTemplate);
    }
  }, [configuredTemplate, invoice?.id]);

  useEffect(() => {
    if (configuredCopies) {
      setSelectedCopiesCount(configuredCopies);
    }
  }, [configuredCopies, invoice?.id]);

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add("invoice-print-open");
    } else {
      document.body.classList.remove("invoice-print-open");
    }
    return () => {
      document.body.classList.remove("invoice-print-open");
    };
  }, [isOpen]);

  const copiesToRender = Array.from({ length: selectedCopiesCount }, (_, i) => i + 1);

  // Financial values
  const isNonGst = invoice.invoiceType === "Normal" || invoice.isNonGst;
  const grandTotal = Number(invoice.grandTotal || 0);
  const totalInWords = numberToIndianWords(grandTotal);
  // Repeated lines of the same item at the same rate print as one row (qty summed)
  const lineItems = mergeLineItemsForPrint(invoice.lineItems || []);
  const subtotal = Number(invoice.subtotal || 0);
  const cgst = Number(invoice.cgst || 0);
  const sgst = Number(invoice.sgst || 0);
  const igst = Number(invoice.igst || 0);
  const roundOff = invoice.roundOff !== undefined ? Number(invoice.roundOff) : 0;

  // GST rate labels derived from line items (e.g. "2.5%", or "2.5%/6%" for mixed rates)
  const gstRates = [...new Set(lineItems.map((li) => Number(li.gstPercent || 0)).filter((r) => r > 0))].sort((a, b) => a - b);
  const formatRates = (divisor) => gstRates.map((r) => `${Number((r / divisor).toFixed(2))}%`).join("/");
  const halfGstLabel = gstRates.length ? ` ${formatRates(2)}` : "";
  const fullGstLabel = gstRates.length ? ` ${formatRates(1)}` : "";

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPdf = async () => {
    setIsDownloadingPdf(true);
    try {
      const wrapperEl = document.getElementById("printable-invoice-wrapper");
      if (!wrapperEl) {
        throw new Error("Printable invoice container not found");
      }
      await downloadInvoicePdf(wrapperEl, invoice);
    } catch (err) {
      console.error("PDF Download failed:", err);
      alert("Could not download PDF: " + (err.message || "Unknown error"));
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  const handleShareWhatsAppPdf = async () => {
    setIsSharingPdf(true);
    try {
      const wrapperEl = document.getElementById("printable-invoice-wrapper");
      if (!wrapperEl) {
        throw new Error("Printable invoice container not found");
      }
      await shareInvoicePdf(wrapperEl, invoice, companyName);
    } catch (err) {
      console.error("PDF Share failed:", err);
      alert("Could not share PDF: " + (err.message || "Unknown error"));
    } finally {
      setIsSharingPdf(false);
    }
  };


  /* =========================================================================
     TEMPLATE 1: Physical Paper Bill Replica (Default)
     ========================================================================= */
  const renderPaperLedger = (copyNum) => (
    <>
      {/* Top Copy Marking & Title Bar */}
      <div className="pl-top-banner">
        <div className="pl-title-center">INVOICE</div>
        <div className="pl-copy-marking">
          {COPY_LABELS[copyNum] || `COPY #${copyNum}`}
        </div>
      </div>

      {/* Bill No and Date Row */}
      <div className="pl-billno-date-row">
        <div className="pl-billno-box">
          <span className="pl-label">BILL NO: </span>
          <span className="pl-val-bold">{invoice.invoiceNo}</span>
        </div>
        <div className="pl-date-box">
          <span className="pl-label">DATE: </span>
          <span className="pl-val-bold">{formattedInvoiceDate}</span>
        </div>
      </div>

      {/* Two Column Seller and Buyer Box */}
      <div className="pl-party-grid">
        {/* Left: Issuing Company (Seller) */}
        <div className="pl-party-col pl-seller-col">
          <div className="pl-company-title">{companyName}</div>
          {tradeName && <div className="pl-trade-name">({tradeName})</div>}
          <div className="pl-addr">{fullAddress}</div>
          {companyGstin && (
            <div className="pl-meta-line">
              <strong>GST NO : </strong> {companyGstin}
            </div>
          )}
        </div>

        {/* Right: M/S Customer (Buyer) */}
        <div className="pl-party-col pl-buyer-col">
          <div className="pl-buyer-header">M/s. {invoice.customerName}</div>
          <div className="pl-addr">
            {invoice.shippingAddress?.addressLine1 || invoice.customerAddress || invoice.customerCity}
            {invoice.customerCity && !invoice.customerAddress ? `, ${invoice.customerCity}` : ""}
            {invoice.customerState ? `, ${invoice.customerState}` : ""}
          </div>
          {invoice.customerGstin && (
            <div className="pl-meta-line">
              <strong>GST NO : </strong> {invoice.customerGstin}
            </div>
          )}
        </div>
      </div>

      {/* Contact & Lorry Transport Bar */}
      <div className="pl-lorry-bar">
        <div>
          <span className="pl-label">Cell no : </span>
          <span className="pl-val-bold">{companyPhone || "-"}</span>
        </div>
        <div>
          <span className="pl-label">Lorry No : </span>
          <span className="pl-val-bold">{invoice.lorryNo || "-"}</span>
        </div>
      </div>

      {/* Itemized Table */}
      <div className="pl-table-wrapper">
        <table className="pl-table">
          <thead>
            <tr>
              <th style={{ width: "12%", textAlign: "right" }}>RATE</th>
              <th style={{ width: "42%", textAlign: "left" }}>PARTICULARS</th>
              <th style={{ width: "16%", textAlign: "center" }}>HSN CODE</th>
              <th style={{ width: "14%", textAlign: "right" }}>KGS</th>
              <th style={{ width: "16%", textAlign: "right" }}>AMOUNT</th>
            </tr>
          </thead>
          <tbody>
            {lineItems.map((item, idx) => (
              <tr key={idx} className="pl-item-row">
                <td style={{ textAlign: "right" }}>
                  {Number(item.rate).toFixed(2)}
                </td>
                <td style={{ textAlign: "left", fontWeight: "600" }}>
                  {item.itemName}
                </td>
                <td style={{ textAlign: "center" }}>
                  {item.gstCode || "-"}
                </td>
                <td style={{ textAlign: "right" }}>
                  {Number(item.qty).toLocaleString("en-IN")}
                </td>
                <td style={{ textAlign: "right", fontWeight: "600" }}>
                  {Number(item.lineSubtotal || item.lineTotal).toFixed(2)}
                </td>
              </tr>
            ))}
            {/* Ruled notebook rows with box borders maintaining authentic ledger grid structure */}
            {Array.from({ length: Math.max(0, 13 - lineItems.length) }).map((_, emptyIdx) => (
              <tr key={`empty-${emptyIdx}`} className="pl-ruled-row">
                <td style={{ textAlign: "right" }}>&nbsp;</td>
                <td style={{ textAlign: "left" }}>&nbsp;</td>
                <td style={{ textAlign: "center" }}>&nbsp;</td>
                <td style={{ textAlign: "right" }}>&nbsp;</td>
                <td style={{ textAlign: "right" }}>&nbsp;</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Unified Bottom Footer Section: Totals, Bank Details & Signatures */}
      <div className="pl-footer-section">
        <div className="pl-totals-container">
          <div className="pl-totals-left">
            <div className="pl-amount-in-words-box">
              <div className="pl-amount-words-title">RUPEES IN WORDS</div>
              <div className="pl-amount-words-text">{totalInWords}</div>
            </div>
            {bankName && (
              <div className="pl-bank-box">
                <div style={{ fontWeight: "700", textDecoration: "underline", marginBottom: "2px" }}>
                  BANK DETAILS
                </div>
                <div>Bank: <strong>{bankName}</strong> {branchName ? `(${branchName})` : ""}</div>
                <div>A/C No: <strong>{accountNumber}</strong></div>
                <div>IFSC: <strong>{ifscCode}</strong></div>
              </div>
            )}
          </div>

          <div className="pl-totals-right">
            <div className="pl-calc-row">
              <span className="pl-calc-label">TOTAL</span>
              <span className="pl-calc-val">{subtotal.toFixed(2)}</span>
            </div>

            {!isNonGst && (
              <>
                {invoice.isInterState ? (
                  <div className="pl-calc-row">
                    <span className="pl-calc-label">IGST{fullGstLabel}</span>
                    <span className="pl-calc-val">{igst.toFixed(2)}</span>
                  </div>
                ) : (
                  <>
                    <div className="pl-calc-row">
                      <span className="pl-calc-label">
                        CGST{halfGstLabel}
                      </span>
                      <span className="pl-calc-val">{cgst.toFixed(2)}</span>
                    </div>
                    <div className="pl-calc-row">
                      <span className="pl-calc-label">
                        SGST{halfGstLabel}
                      </span>
                      <span className="pl-calc-val">{sgst.toFixed(2)}</span>
                    </div>
                  </>
                )}
              </>
            )}

            {roundOff !== 0 && (
              <div className="pl-calc-row">
                <span className="pl-calc-label">ROUND OFF</span>
                <span className="pl-calc-val">{roundOff > 0 ? `+${roundOff.toFixed(2)}` : roundOff.toFixed(2)}</span>
              </div>
            )}

            <div className="pl-calc-row pl-grand-total-row">
              <span className="pl-calc-label">GRAND TOTAL</span>
              <span className="pl-calc-val">₹{grandTotal.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Signatory Footer Box */}
        <div className="pl-signatory-grid">
          <div className="pl-customer-sign-box">
            <div className="pl-sign-placeholder" />
            <div className="pl-sign-text">Customer's Signature</div>
          </div>

          <div className="pl-authorized-sign-box">
            <div className="pl-for-company">For {companyName}</div>
            <div className="pl-signature-image-area">
              {signatureUrl ? (
                <img
                  src={signatureUrl}
                  alt="Authorized Signature"
                  className="pl-signature-img"
                />
              ) : (
                <div className="pl-sign-placeholder" />
              )}
            </div>
            <div className="pl-sign-text">Authorized Signatory</div>
          </div>
        </div>
      </div>
    </>
  );

  /* =========================================================================
     TEMPLATE 2: Modern Tax Invoice (Corporate Layout)
     ========================================================================= */
  const renderModernTax = (copyNum) => {
    // Billing Address Details
    const rawBillStreet = invoice.billingAddress?.addressLine1 || invoice.customerAddress || "";
    const billCity = invoice.billingAddress?.city || invoice.customerCity || "";
    const billState = invoice.billingAddress?.state || invoice.customerState || "";
    const billPincode = invoice.billingAddress?.pincode || "";
    const billStreet = rawBillStreet.trim().toLowerCase() === billCity.trim().toLowerCase() ? "" : rawBillStreet;
    const billCityState = [
      billCity,
      billState ? (billPincode ? `${billState} - ${billPincode}` : billState) : billPincode
    ].filter(Boolean).join(", ");

    // Shipping Address Details
    const rawShipStreet = invoice.shippingAddress?.addressLine1 || rawBillStreet;
    const shipCity = invoice.shippingAddress?.city || billCity;
    const shipState = invoice.shippingAddress?.state || billState;
    const shipPincode = invoice.shippingAddress?.pincode || billPincode;
    const shipStreet = rawShipStreet.trim().toLowerCase() === shipCity.trim().toLowerCase() ? "" : rawShipStreet;
    const shipCityState = [
      shipCity,
      shipState ? (shipPincode ? `${shipState} - ${shipPincode}` : shipState) : shipPincode
    ].filter(Boolean).join(", ");

    const shipLabel = invoice.shippingAddress?.label;
    const hasCustomShipLabel = shipLabel && shipLabel !== "Billing Address" && shipLabel !== "Primary Destination";

    return (
    <div className="modern-tax-container">
      {/* Modern Header Bar */}
      <div className="mt-header-row">
        <div>
          <h1 className="mt-company-name">{companyName}</h1>
          {tradeName && <div className="mt-trade">{tradeName}</div>}
          <div className="mt-sub">{fullAddress}</div>
          <div className="mt-sub">GSTIN: <strong>{companyGstin || "N/A"}</strong> • Phone: {companyPhone}</div>
        </div>
        <div className="mt-badge-box">
          <div className="mt-inv-title">{isNonGst ? "BILL OF SUPPLY" : "TAX INVOICE"}</div>
          <div className="mt-copy-badge">{COPY_LABELS[copyNum] || `COPY #${copyNum}`}</div>
          <div className="mt-meta-chip"># {invoice.invoiceNo}</div>
          <div className="mt-date">Date: {formattedInvoiceDate}</div>
        </div>
      </div>

      <div className="mt-divider" />

      {/* Modern Bill To / Ship To Grid - Symmetric Layout */}
      <div className="mt-party-grid">
        <div className="mt-party-box">
          <div className="mt-section-title">Billed To</div>
          <div className="mt-party-name">{invoice.customerName}</div>
          {billStreet && <div className="mt-sub">{billStreet}</div>}
          {billCityState && <div className="mt-sub">{billCityState}</div>}
          {invoice.customerGstin && <div className="mt-sub">GSTIN: <strong>{invoice.customerGstin}</strong></div>}
        </div>
        <div className="mt-party-box">
          <div className="mt-section-title">Shipped Destination</div>
          <div className="mt-party-name">
            {invoice.customerName}
            {hasCustomShipLabel && (
              <span style={{ fontSize: "0.82em", fontWeight: "normal", color: "#4b5563" }}>
                {" "}({shipLabel})
              </span>
            )}
          </div>
          {shipStreet && <div className="mt-sub">{shipStreet}</div>}
          {shipCityState && <div className="mt-sub">{shipCityState}</div>}
          {invoice.lorryNo && <div className="mt-sub">Lorry No: <strong>{invoice.lorryNo}</strong></div>}
        </div>
      </div>

      {/* Modern Table */}
      <table className="mt-table">
        <thead>
          <tr>
            <th style={{ width: "5%" }}>#</th>
            <th style={{ width: "40%", textAlign: "left" }}>Item & Description</th>
            <th style={{ width: "15%", textAlign: "center" }}>HSN / SAC</th>
            <th style={{ width: "12%", textAlign: "right" }}>Qty</th>
            <th style={{ width: "14%", textAlign: "right" }}>Rate</th>
            <th style={{ width: "14%", textAlign: "right" }}>Amount</th>
          </tr>
        </thead>
        <tbody>
          {lineItems.map((li, idx) => (
            <tr key={idx}>
              <td>{idx + 1}</td>
              <td style={{ textAlign: "left", fontWeight: "600" }}>{li.itemName}</td>
              <td style={{ textAlign: "center" }}>{li.gstCode || "-"}</td>
              <td style={{ textAlign: "right" }}>{li.qty} {li.unit || "pcs"}</td>
              <td style={{ textAlign: "right" }}>₹{Number(li.rate).toFixed(2)}</td>
              <td style={{ textAlign: "right", fontWeight: "700" }}>₹{Number(li.lineSubtotal || li.lineTotal).toFixed(2)}</td>
            </tr>
          ))}
          {/* Dummy rows to fill the gap on A4 sheet */}
          {Array.from({ length: Math.max(0, 10 - lineItems.length) }).map((_, emptyIdx) => (
            <tr key={`mt-empty-${emptyIdx}`} className="mt-empty-row">
              <td>&nbsp;</td>
              <td>&nbsp;</td>
              <td>&nbsp;</td>
              <td>&nbsp;</td>
              <td>&nbsp;</td>
              <td>&nbsp;</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Modern Summary & Signatory Anchored to Bottom */}
      <div className="mt-footer-section">
        <div className="mt-bottom-grid">
          <div className="mt-bottom-left">
            <div className="mt-words-title">Total in Words:</div>
            <div className="mt-words-val">{totalInWords}</div>
            {bankName && (
              <div className="mt-bank-chip">
                <strong>Bank Remittance:</strong> {bankName} • A/C: {accountNumber} • IFSC: {ifscCode}
              </div>
            )}
          </div>
          <div className="mt-bottom-right">
            <div className="mt-calc-line"><span>Subtotal:</span> <strong>₹{subtotal.toFixed(2)}</strong></div>
            {!isNonGst && (
              <>
                {invoice.isInterState ? (
                  <div className="mt-calc-line"><span>IGST{fullGstLabel}:</span> <strong>₹{igst.toFixed(2)}</strong></div>
                ) : (
                  <>
                    <div className="mt-calc-line"><span>CGST{halfGstLabel}:</span> <strong>₹{cgst.toFixed(2)}</strong></div>
                    <div className="mt-calc-line"><span>SGST{halfGstLabel}:</span> <strong>₹{sgst.toFixed(2)}</strong></div>
                  </>
                )}
              </>
            )}
            {roundOff !== 0 && <div className="mt-calc-line"><span>Round Off:</span> <strong>₹{roundOff.toFixed(2)}</strong></div>}
            <div className="mt-calc-line mt-total-hero">
              <span>Grand Total:</span>
              <span>₹{grandTotal.toFixed(2)}</span>
            </div>
          </div>
        </div>

        <div className="mt-sign-row">
          <div className="mt-terms">Thank you for your business!</div>
          <div className="mt-sign-right">
            <div style={{ fontSize: "0.82rem", fontWeight: "700" }}>For {companyName}</div>
            {signatureUrl ? (
              <img
                src={signatureUrl}
                alt="Authorized Signature"
                crossOrigin="anonymous"
                style={{
                  maxHeight: "55px",
                  maxWidth: "160px",
                  objectFit: "contain",
                  margin: "4px auto 2px auto",
                  display: "block",
                  mixBlendMode: "multiply"
                }}
              />
            ) : (
              <div style={{ height: "45px" }} />
            )}
            <div style={{ borderTop: "1px solid #999", paddingTop: "2px", fontSize: "0.75rem" }}>Authorized Signatory</div>
          </div>
        </div>
      </div>
    </div>
  );
};

  /* =========================================================================
     TEMPLATE 3: Compact Slip (Condensed Single-Page Format)
     ========================================================================= */
  const renderCompactSlip = (copyNum) => (
    <div className="compact-slip-container">
      <div className="cs-header">
        <div style={{ fontWeight: "900", fontSize: "1.2rem" }}>{companyName}</div>
        <div style={{ fontSize: "0.75rem" }}>{fullAddress} • GSTIN: {companyGstin || "Unregistered"}</div>
        <div style={{ display: "flex", justifyContent: "space-between", borderTop: "1px dashed #333", borderBottom: "1px dashed #333", padding: "4px 0", marginTop: "4px", fontWeight: "700", fontSize: "0.8rem" }}>
          <span>BILL #: {invoice.invoiceNo}</span>
          <span>DATE: {formattedInvoiceDate}</span>
          <span>{COPY_LABELS[copyNum] || `COPY #${copyNum}`}</span>
        </div>
        <div style={{ fontSize: "0.8rem", textAlign: "left", padding: "4px 0" }}>
          CUSTOMER: <strong>{invoice.customerName}</strong> {invoice.customerCity ? `(${invoice.customerCity})` : ""}
          {invoice.lorryNo && ` • Lorry: ${invoice.lorryNo}`}
        </div>
      </div>

      <table className="cs-table">
        <thead>
          <tr>
            <th style={{ textAlign: "left" }}>Item</th>
            <th style={{ textAlign: "right" }}>Qty</th>
            <th style={{ textAlign: "right" }}>Rate</th>
            <th style={{ textAlign: "right" }}>Total</th>
          </tr>
        </thead>
        <tbody>
          {lineItems.map((li, idx) => (
            <tr key={idx}>
              <td style={{ textAlign: "left" }}>{li.itemName}</td>
              <td style={{ textAlign: "right" }}>{li.qty} {li.unit}</td>
              <td style={{ textAlign: "right" }}>₹{Number(li.rate).toFixed(2)}</td>
              <td style={{ textAlign: "right", fontWeight: "700" }}>₹{Number(li.lineSubtotal || li.lineTotal).toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Compact Slip Footer Anchored to Bottom */}
      <div className="cs-footer-section">
        <div style={{ borderTop: "1px dashed #333", paddingTop: "4px", fontSize: "0.82rem" }}>
          <div style={{ display: "flex", justifyContent: "space-between" }}><span>Subtotal:</span> <span>₹{subtotal.toFixed(2)}</span></div>
          {!isNonGst && <div style={{ display: "flex", justifyContent: "space-between" }}><span>Tax (CGST+SGST/IGST):</span> <span>₹{(cgst + sgst + igst).toFixed(2)}</span></div>}
          <div style={{ display: "flex", justifyContent: "space-between", fontWeight: "900", fontSize: "0.95rem", borderTop: "1px solid #000", marginTop: "4px", paddingTop: "2px" }}>
            <span>TOTAL:</span>
            <span>₹{grandTotal.toFixed(2)}</span>
          </div>
          <div style={{ fontSize: "0.72rem", fontWeight: "700", marginTop: "4px" }}>
            Rupees: {totalInWords}
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginTop: "1.5rem" }}>
          <div style={{ fontSize: "0.7rem" }}>Customer Sign</div>
          <div style={{ textAlign: "right" }}>
            {signatureUrl && <img src={signatureUrl} alt="Sign" style={{ maxHeight: "38px", maxWidth: "100px", display: "block", marginLeft: "auto" }} />}
            <div style={{ fontSize: "0.7rem", borderTop: "1px solid #000", minWidth: "110px" }}>For {companyName}</div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title={watermark ? `${watermark} • ${invoice?.invoiceNo || ""}` : "Invoice PDF Print Preview"} size="lg" className="invoice-print-sheet">
      <div className="print-modal-container">
        {/* Controls Toolbar (Hidden when printing) */}
        <div className="print-controls-bar no-print">
          <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap", alignItems: "center" }}>
            <div>
              <label style={{ fontSize: "0.75rem", fontWeight: "700", color: "var(--text-dim)", display: "block", marginBottom: "2px" }}>
                TEMPLATE
              </label>
              <select
                value={selectedTemplate}
                onChange={(e) => setSelectedTemplate(e.target.value)}
                className="form-control"
                style={{ minHeight: "36px", padding: "0.3rem 0.6rem", fontSize: "0.85rem" }}
              >
                {INVOICE_TEMPLATES.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ fontSize: "0.75rem", fontWeight: "700", color: "var(--text-dim)", display: "block", marginBottom: "2px" }}>
                COPIES TO PRINT
              </label>
              <select
                value={selectedCopiesCount}
                onChange={(e) => setSelectedCopiesCount(Number(e.target.value))}
                className="form-control"
                style={{ minHeight: "36px", padding: "0.3rem 0.6rem", fontSize: "0.85rem" }}
              >
                <option value={1}>1 Copy (Original Only)</option>
                <option value={2}>2 Copies (Original + Duplicate)</option>
                <option value={3}>3 Copies (Original + Duplicate + Triplicate)</option>
              </select>
            </div>

            <div>
              <label style={{ fontSize: "0.75rem", fontWeight: "700", color: "var(--text-dim)", display: "block", marginBottom: "2px" }}>
                VIEW
              </label>
              <button
                type="button"
                className={`btn btn-sm ${fitScreen ? "btn-primary" : "btn-secondary"}`}
                onClick={() => setFitScreen(!fitScreen)}
                style={{ minHeight: "36px", padding: "0.3rem 0.75rem", fontSize: "0.85rem", fontWeight: "600" }}
                title={fitScreen ? "Switch to 100% Original Size" : "Fit invoice to screen width"}
              >
                {fitScreen ? "🔍 100% Size" : "📱 Fit Screen"}
              </button>
            </div>
          </div>

          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", justifyContent: "flex-end" }}>
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
            {!watermark && (<>
            <Button
              variant="secondary"
              onClick={handleDownloadPdf}
              disabled={isDownloadingPdf || isSharingPdf}
              style={{
                fontWeight: "600",
                display: "inline-flex",
                alignItems: "center",
                gap: "0.4rem"
              }}
            >
              {isDownloadingPdf ? "⏳ Downloading..." : "⬇️ Download PDF"}
            </Button>
            <Button
              variant="primary"
              onClick={handleShareWhatsAppPdf}
              disabled={isSharingPdf || isDownloadingPdf}
              style={{
                background: "#16a34a",
                color: "#ffffff",
                borderColor: "#16a34a",
                fontWeight: "600",
                display: "inline-flex",
                alignItems: "center",
                gap: "0.4rem"
              }}
            >
              {isSharingPdf ? "⏳ Generating PDF..." : "💬 Share PDF on WhatsApp"}
            </Button>
            <Button variant="primary" onClick={handlePrint}>
              🖨️ Print / Save as PDF
            </Button>
            </>)}
          </div>
        </div>

        {watermark && (
          <div className="no-print" data-testid="old-version-banner" style={{ margin: "0.5rem 0", padding: "0.5rem 0.75rem", borderRadius: "var(--radius-md)", background: "rgba(245, 158, 11, 0.15)", border: "1px solid rgba(245, 158, 11, 0.5)", color: "var(--warning, #b45309)", fontWeight: 700, textAlign: "center" }}>
            📜 {watermark} — this is how the invoice looked before it was edited
          </div>
        )}

        {/* Printable Document Container */}
        <div id="printable-invoice-wrapper" className={`printable-invoice-wrapper ${fitScreen ? "fit-screen-mode" : ""}`}>
          {copiesToRender.map((copyNum, index) => (
            <div
              key={copyNum}
              className={`printable-page template-${selectedTemplate} ${index > 0 ? "page-break-before" : ""}`}
            >
              {(() => {
                const customTemplateObj = getInvoiceTemplateById(selectedTemplate);
                const CustomComponent = customTemplateObj?.component;
                if (CustomComponent) {
                  return (
                    <CustomComponent
                      invoice={invoice}
                      company={company}
                      copyNum={copyNum}
                      lineItems={lineItems}
                      formattedInvoiceDate={formattedInvoiceDate}
                      totalInWords={totalInWords}
                      isNonGst={isNonGst}
                      grandTotal={grandTotal}
                      subtotal={subtotal}
                      cgst={cgst}
                      sgst={sgst}
                      igst={igst}
                      roundOff={roundOff}
                    />
                  );
                }
                if (selectedTemplate === "modern-tax") {
                  return renderModernTax(copyNum);
                }
                if (selectedTemplate === "compact") {
                  return renderCompactSlip(copyNum);
                }
                return renderPaperLedger(copyNum);
              })()}
            </div>

          ))}
        </div>
      </div>
    </BottomSheet>
  );
}

export default InvoicePrintModal;
